import React, {
  useEffect, useMemo, useState, useContext,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { Container, Stack, styled } from '@mui/system';
import {
  CircularProgress, Grid, Paper, Typography,
} from '@mui/material';
import JsxParser from 'react-jsx-parser';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { getInviteFormByPathGql, getInviteGql } from '../InviteResponse/gql';
import RsvpHeading from '../../Components/RsvpHeading';
import { setMarketingOptOutLegacyGql, setMarketingOptOutWithCodeGql } from './gql';
import GlobalStyles from '../../Components/GlobalStyles';
import { GeneralContext } from '../../util/Store';
import { ThemeContext } from '../../util/ThemeStore';
import ExternalLink from '../../Components/ExternalLink';

const PaperBox = styled(Paper)({
  padding: '36px 36px 36px 36px',
  paddingBottom: 10,
  position: 'relative',
});

const ErrorTypo = styled(Typography)({
  textAlign: 'center',
  marginTop: '35vh',
});

let requestTriggered = false;

export default function MarketingOptout() {
  const { inviteCode, path, optoutCode } = useParams();
  const [statusCode, setStatusCode] = useState();
  const snackbar = useSnackbar();
  const [acceptSuccessful, setAcceptSuccessful] = useState(null);
  const generalStore = useContext(GeneralContext);
  const themeStore = useContext(ThemeContext);

  const {
    loading,
  } = useQuery(getInviteGql, {
    fetchPolicy: 'no-cache',
    variables: {
      inviteCode,
    },
    onError: () => snackbar.showMessage('Fehler beim Laden.'),
    onCompleted: ({ getInvite }) => {
      generalStore.setFooterLinks(getInvite.inviteForm?.frontendSettings?.footerLinks);
    },
  });

  const { data: inviteFormResult, loading: formLoading } = useQuery(getInviteFormByPathGql, {
    fetchPolicy: 'no-cache',
    variables: { path },
    onCompleted: (result) => {
      const form = result?.getInviteFormByPath?.inviteForm;
      generalStore.setFooterLinks(form?.frontendSettings?.footerLinks);
      if (result?.getInviteFormByPath?.themeConfig?.typography) {
        themeStore.setTypography(result?.getInviteFormByPath?.themeConfig?.typography);
      }
    },
  });

  const [setMarketingOptOutLegacy, { loading: setMarketingOptOutLegacyLoading }] = useMutation(setMarketingOptOutLegacyGql, {
    onCompleted: () => {
      setAcceptSuccessful(true);
    },
    onError: (err) => {
      setStatusCode(err.networkError.statusCode);
    },
  });

  const [setMarketingOptOutWithCode, { loading: setMarketingOptOutWithCodeLoading }] = useMutation(setMarketingOptOutWithCodeGql, {
    onCompleted: () => {
      setAcceptSuccessful(true);
    },
    onError: (err) => {
      setStatusCode(err.networkError.statusCode);
    },
  });
  const setMarketingOptOutLoading = setMarketingOptOutLegacyLoading || setMarketingOptOutWithCodeLoading;

  const inviteForm = useMemo(() => inviteFormResult?.getInviteFormByPath?.inviteForm, [inviteFormResult?.getInviteFormByPath?.inviteForm]);
  useEffect(() => {
    if (requestTriggered) return;
    if (optoutCode) {
      setMarketingOptOutWithCode({
        variables: {
          inviteCode,
          optoutCode,
          input: {},
        },
      });
    } else {
      setMarketingOptOutLegacy({
        variables: {
          inviteCode,
          input: {},
        },
      });
    }
    requestTriggered = true;
  }, [inviteCode, optoutCode, setMarketingOptOutLegacy, setMarketingOptOutWithCode]);

  if (!inviteForm || formLoading || loading) {
    return (
      <Stack alignItems="center" sx={{ mt: 5 }}>
        <CircularProgress />
      </Stack>
    );
  }

  const renderContent = () => {
    if (setMarketingOptOutLoading) {
      // Show loading spinner while the mutation is in progress
      return (
        <Stack alignItems="center" sx={{ mt: 5 }}>
          <CircularProgress />
        </Stack>
      );
    }

    if (acceptSuccessful) {
      const marketingOptOutHeadline = inviteForm?.frontendSettings?.marketingOptOutHeadline || 'Sie haben Ihre Einwilligung widerrufen.';
      const marketingOptOutDescription = inviteForm?.frontendSettings?.marketingOptOutDescription;

      // Success case
      return (
        <Grid container direction="column" alignItems="center" sx={{ mb: 2 }}>
          <Grid container direction="row" justifyContent="center" sx={{ mb: 2 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80 }} />
          </Grid>
          <Typography sx={{ textAlign: 'center', my: 2 }} variant="h4">
            {marketingOptOutHeadline}
          </Typography>
          {marketingOptOutDescription ? (
            <JsxParser
              components={{
                Typography, Grid, Link: ExternalLink, Stack,
              }}
              jsx={marketingOptOutDescription}
              renderInWrapper={false}
              autoCloseVoidElements
            />
          ) : (
            <Typography variant="subtitle1" sx={{ mt: 2, textAlign: 'center' }}>
              Ihre Kontaktdaten wurden aus unserem Verteiler für Marketinginformationen entfernt.
              <br />
              Selbstverständlich erhalten Sie keine weiteren Werbemitteilungen mehr von Umwelttechnik BW.
            </Typography>
          )}
        </Grid>
      );
    }

    if (!statusCode) return null;

    // Error cases
    let errorMessage = 'Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
    switch (statusCode) {
      case 400:
        errorMessage = 'Ungültiger oder abgelaufener Code/Link.';
        break;
      case 404:
        errorMessage = 'Ungültiger Link.';
        break;
      case 409:
        errorMessage = 'Die Einwilligung wurde bereits widerrufen.';
        break;
      default:
        break;
    }

    return (
      <Grid container direction="column" alignItems="center" sx={{ mb: 2 }}>
        <HighlightOffIcon color="error" sx={{ fontSize: 80 }} />
        <Typography sx={{ textAlign: 'center', my: 2 }} variant="h4">
          {errorMessage}
        </Typography>
      </Grid>
    );
  };

  if (!inviteCode) return (<ErrorTypo variant="h2">Fehlerhafter Link</ErrorTypo>);
  return (
    <Container maxWidth="lg">
      <GlobalStyles
        colors={inviteFormResult?.getInviteFormByPath?.colors}
        customCss={inviteFormResult?.getInviteFormByPath?.customCss}
        font={inviteFormResult?.getInviteFormByPath?.font}
      />
      <RsvpHeading logoUrl={inviteFormResult?.getInviteFormByPath?.logoUrl} inviteForm={inviteForm} />
      <PaperBox sx={{ pb: 4, mb: 2 }} elevation={4}>
        {renderContent()}
      </PaperBox>
    </Container>
  );
}
